import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import { WizardHeader } from '../../../../../common/components/wizardHeader/WizardHeader'

import { WaiverFieldType, WaiverSection } from '@percent/domain/volunteering'
import {
  AcknowledgeModal,
  Box,
  Flex,
  FullPageFormLayout,
  FormField,
  Loader,
  Modal,
  Spacer,
  TextInput,
  getInputStatus
} from '@percent/lemonade'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { useMutation, useQuery } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import styles from './WaiverTemplateForm.module.scss'

const TEMPLATE_NAME_MAX_CHARS = 255
const WAIVER_CONTENT_MAX_CHARS = 30000
// Returning here remounts TemplatesContainer, which refetches the list on mount.
const LIST_ROUTE = '/workplace-giving/templates?tab=waiver'

const hasWaiverContent = (html: string) => html.replace(/<[^>]*>/g, '').trim().length > 0

type WaiverTemplateFormValues = {
  name: string
  content: string
}

export function WaiverTemplateForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { waiverTemplateService } = useServices()
  const { templateId } = useParams<{ templateId: string }>()
  const isEdit = !!templateId

  const [resultState, setResultState] = useState<'success' | 'failure' | undefined>(undefined)

  const [{ data, isLoading: isFetching, errorMessage }] = useQuery(
    waiverTemplateService.getWaiverTemplate,
    templateId ?? '',
    isEdit,
    isEdit
  )

  const [{ isLoading: isCreating }, { apiFunc: createTemplate }] = useMutation(
    waiverTemplateService.createWaiverTemplate,
    () => setResultState('success'),
    () => setResultState('failure')
  )

  const [{ isLoading: isUpdating }, { apiFunc: updateTemplate }] = useMutation(
    waiverTemplateService.updateWaiverTemplate,
    () => setResultState('success'),
    () => setResultState('failure')
  )

  const initialName = data?.name ?? ''
  const initialContent = data?.fields?.find(field => field.type === WaiverFieldType.Text)?.content.text ?? ''

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .required(t('errorMessage.required'))
          .max(TEMPLATE_NAME_MAX_CHARS, t('errorMessage.shouldNotExceed255')),
        content: Yup.string()
          .required(t('errorMessage.required'))
          .max(WAIVER_CONTENT_MAX_CHARS, t('errorMessage.waiverContentTooLong', { max: WAIVER_CONTENT_MAX_CHARS }))
          .test('non-empty-content', t('errorMessage.required'), value => hasWaiverContent(value ?? ''))
      }),
    [t]
  )

  const { control, handleSubmit, reset } = useForm<WaiverTemplateFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: { name: initialName, content: initialContent }
  })

  useEffect(() => {
    if (data) {
      reset({ name: initialName, content: initialContent })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, reset])

  const submit = handleSubmit(({ name, content }) => {
    const fields = [{ type: WaiverFieldType.Text, required: true, content: { text: content } }]

    if (isEdit && templateId) {
      updateTemplate({ templateId, name: name.trim(), fields })

      return
    }

    createTemplate({ name: name.trim(), fields })
  })

  const watchedName = useWatch({ control, name: 'name' })
  const watchedContent = useWatch({ control, name: 'content' })
  const isSaving = isCreating || isUpdating
  const isEmpty = !watchedName?.trim() || !hasWaiverContent(watchedContent ?? '')

  const isUnchanged = isEdit && watchedName?.trim() === initialName.trim() && watchedContent === initialContent

  const showLoader = isEdit && isFetching
  const showError = isEdit && !isFetching && (!!errorMessage || !data)
  const isSubmitDisabled = isFetching || isEmpty || isUnchanged || showError

  const handleBack = () => navigate(LIST_ROUTE)
  const handleFailureClose = () => setResultState(undefined)

  return (
    <Box className={styles.pageWrapper} data-testid="waiver-template-form">
      <FullPageFormLayout
        actionsHeader={
          <WizardHeader
            title={
              isEdit ? t('typography.templates.form.editWaiverTitle') : t('typography.templates.form.createWaiverTitle')
            }
            onCancel={handleBack}
            onPublish={submit}
            isPublishDisabled={isSubmitDisabled}
            isLoading={isSaving}
            publishBtnCopy={isEdit ? t('button.save') : t('button.create')}
            toolTipCopy={isSubmitDisabled ? t('toolTip.requiredFields') : ''}
            publishBtnTestId="waiver-template-submit-button"
            cancelBtnTestId="waiver-template-cancel-button"
          />
        }
      >
        {showLoader && <Loader />}
        {showError && <ErrorView errorMessage={errorMessage} />}
        {!showLoader && !showError && (
          <Flex direction="column" className={styles.formWrapper}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => {
                const nameErrorMessage = fieldState.isTouched ? fieldState.error?.message : undefined

                return (
                  <FormField
                    label={t('typography.templates.form.nameLabel')}
                    necessity="required"
                    status={getInputStatus(nameErrorMessage)}
                    statusMessage={nameErrorMessage}
                  >
                    <TextInput
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={t('typography.templates.form.namePlaceholder')}
                      maxLength={TEMPLATE_NAME_MAX_CHARS}
                      data-testid="waiver-template-name-input"
                    />
                  </FormField>
                )
              }}
            />

            <Spacer size={6} axis="vertical" />

            <Controller
              control={control}
              name="content"
              render={({ field, fieldState }) => (
                <WaiverSection
                  waiver={{ waiverText: field.value, waiverEnabled: true }}
                  fieldNames={{ text: 'content' }}
                  setFieldValue={(_, value) => field.onChange(value)}
                  setFieldTouched={() => field.onBlur()}
                  handleBlur={() => field.onBlur()}
                  errors={{ content: fieldState.error?.message }}
                  touched={{ content: fieldState.isTouched }}
                  hideEnabledToggle
                  textLabel={t('typography.templates.form.waiverContentLabel')}
                  textDescription={t('typography.templates.form.waiverContentDescription')}
                  textPlaceholder={t('typography.templates.form.waiverContentPlaceholder')}
                />
              )}
            />
          </Flex>
        )}
      </FullPageFormLayout>

      <Modal
        open={!!resultState}
        onClose={resultState === 'success' ? handleBack : handleFailureClose}
        data-testid="waiver-template-result-modal"
      >
        {resultState === 'success' && (
          <AcknowledgeModal
            result="positive"
            title={
              isEdit
                ? t('typography.templates.form.editSuccessTitle')
                : t('typography.templates.form.createSuccessTitle')
            }
            description={t('typography.templates.form.saveSuccessDescription')}
            buttonText={t('button.close')}
            handleClose={handleBack}
            viewTestId="waiver-template-success"
            buttonTestId="waiver-template-success-close"
          />
        )}
        {resultState === 'failure' && (
          <AcknowledgeModal
            result="negative"
            title={
              isEdit ? t('typography.templates.form.editErrorTitle') : t('typography.templates.form.createErrorTitle')
            }
            description={t('typography.templates.form.saveErrorDescription')}
            buttonText={t('button.close')}
            handleClose={handleFailureClose}
            viewTestId="waiver-template-error"
            buttonTestId="waiver-template-error-close"
          />
        )}
      </Modal>
    </Box>
  )
}
